import type { AccountInfo } from '@azure/msal-browser';
import { create } from 'zustand';

type AuthStore = {
  /** The active user account */
  account: AccountInfo | null;
  /** The current access token */
  accessToken: string | null;
  /** The token expiry time */
  tokenExpiry: number | null;
  /**
   * Sets the authentication state
   * @param account - The active user account
   * @param accessToken - The current access token
   * @param expiresOn - The token expiry time in milliseconds since epoch
   */
  setAuth: (account: AccountInfo, accessToken: string, expiresOn: number) => void;
  /** Clears the authentication state */
  clearAuth: () => void;
  /** Invalidates the cached token without clearing the account, allowing recovery via silent refresh */
  clearToken: () => void;
  /** Checks if the current token is valid */
  isTokenValid: () => boolean;
};

export const useAuthStore = create<AuthStore>((set, get) => ({
  account: null,
  accessToken: null,
  tokenExpiry: null,

  setAuth: (account, accessToken, expiresOn) => {
    set({
      account,
      accessToken,
      tokenExpiry: expiresOn,
    });
  },

  clearAuth: () => {
    set({
      account: null,
      accessToken: null,
      tokenExpiry: null,
    });
  },

  clearToken: () => {
    set({ accessToken: null, tokenExpiry: null });
  },

  isTokenValid: () => {
    const { tokenExpiry } = get();
    if (!tokenExpiry) return false;

    // Check if token expires in the next 5 minutes (300 seconds)
    const fiveMinutesFromNow = Date.now() + 5 * 60 * 1000;
    return tokenExpiry > fiveMinutesFromNow;
  },
}));
